import {
  InputLabel,
  TextField,
  Typography,
  Box,
  IconButton,
  Stack,
  Popper,
  Paper,
  MenuItem,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { useContext, useState, useEffect, ChangeEvent, useRef, KeyboardEvent } from 'react';
import { ActivityContext } from '@/contexts/ActivityContext';
import { useTranslation } from 'react-i18next';
import { validateName } from '@/components/utils/Validate';
import SuggestionCard from '@/components/activity/createActivity/SuggestionCard';
import { Activity, ActivityApi, AiApi, languageKey } from '@api';
import { Suggestion } from '@/api';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import debounce from 'debounce';
import { getActivityColor } from '@/components/utils/CalendarFns';
import AssistantSuggestionPopper from '@/components/assistant/AssistantSuggestionPopper';
import AssistantSuggestion from '@/components/assistant/AssistantSuggestion';

type TitleFormProps = {
  errorOnEmpty?: boolean;
  validateForm?: boolean;
  showSuggestions?: boolean;
};

const TitleForm = ({
  errorOnEmpty = false,
  validateForm = true,
  showSuggestions = true,
}: TitleFormProps) => {
  const formHandler = useContext(ActivityFormContext);
  const { activity, setActivity } = useContext(ActivityContext);
  const { currentClient } = useContext(SupervisorContext);
  const [error, setError] = useState<boolean | string>(false);
  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const lang = i18n.language.split('-')[0];
  const aiEnabled = localStorage.getItem('aiEnabled') === 'true';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const textFieldWrapperRef = useRef<HTMLDivElement | null>(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const isLanguageKey = (lang: any): lang is languageKey => {
    return ['sv', 'en', 'de', 'fr', 'es', 'it', 'pl', 'nb', 'da', 'fi'].includes(lang);
  };

  const [oldActivities, setOldActivities] = useState<Activity[]>([]);
  const [oldActivitiesLoading, setOldActivitiesLoading] = useState(false);
  const [copyToast, setCopyToast] = useState<{ copyName: string; open: boolean }>({
    copyName: '',
    open: false,
  });

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);

  const debouncedFetchOldActivities = useRef(
    debounce(async (searchTerm: string) => {
      setOldActivitiesLoading(true);
      try {
        const results = await new ActivityApi().getActivitiesByParam({
          param: searchTerm,
          supervisedTag: currentClient ? currentClient.tag : undefined,
        });
        setOldActivities(results || []);
      } catch (error) {
        console.error('Error fetching addresses:', error);
      } finally {
        setOldActivitiesLoading(false);
      }
    }, 200),
  ).current;

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setActivity({ ...activity, activityName: newValue });

    if (!anchorEl && textFieldWrapperRef.current) {
      setAnchorEl(textFieldWrapperRef.current.querySelector('input') as HTMLElement);
    }

    if (!newValue) {
      setOldActivities([]);
      setError(false);
      return;
    }

    if (newValue.length < 2) return setOldActivities([]);
    if (newValue.length > 64) return setError('Address is too long');

    debouncedFetchOldActivities(newValue);
  };

  const [assistantSuggestion, setAssistantSuggestion] = useState<Suggestion | null>(null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantSuggestionError, setAssistantSuggestionError] = useState(false);

  const latestSearchTerm = useRef('');

  useEffect(() => {
    const trimmed = activity.activityName.trim();
    if (trimmed.length > 0) {
      latestSearchTerm.current = trimmed;
      setAssistantLoading(true);
      setAssistantSuggestion(null);
      setAssistantSuggestionError(false);
      debouncedFetchAssistantSuggestion(trimmed);
    } else {
      setAssistantSuggestion(null);
      setAssistantLoading(false);
      setAssistantSuggestionError(false);
    }
  }, [activity.activityName]);

  useEffect(() => {
    return () => {
      debouncedFetchAssistantSuggestion.clear();
    };
  }, []);

  const debouncedFetchAssistantSuggestion = useRef(
    debounce(async (searchTerm: string) => {
      try {
        const result = await new AiApi().activitySuggestions(
          isLanguageKey(lang) ? lang : 'sv',
          searchTerm,
          true,
        );
        if (latestSearchTerm.current !== searchTerm) return;

        if (!result || result.length === 0) {
          setAssistantSuggestionError(true);
          return;
        }

        setAssistantSuggestion(result[0]);
      } catch (error) {
        console.log('Najs');
        setAssistantSuggestionError(true);
      } finally {
        if (latestSearchTerm.current === searchTerm) {
          setAssistantLoading(false);
        }
      }
    }, 1000),
  ).current;

  useEffect(() => {
    const error = validateName(activity.activityName, errorOnEmpty, t);
    if (error === null && activity.activityName.trim().length) {
      validateForm && formHandler.setValid(true);
      setError(false);
    } else {
      validateForm && formHandler.setValid(false);
      if (error) setError(error);
      else setError('');
    }
  }, [activity]);

  useEffect(() => {
    const fetchSuggestion = async () => {
      const suggestions = await new AiApi().activitySuggestions(
        isLanguageKey(lang) ? lang : 'sv',
        activity.activityName,
      );
      setSuggestions(suggestions);
      setSuggestionsLoading(false);
    };
    if (aiEnabled) fetchSuggestion();
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [oldActivities]);

  const refreshSuggestions = async () => {
    if (suggestionsLoading) return;
    setSuggestionsLoading(true);
    setSuggestions([]);
    const suggestions = await new AiApi().activitySuggestions(
      isLanguageKey(lang) ? lang : 'sv',
      activity.activityName,
      true,
    );
    setSuggestions(suggestions);
    setSuggestionsLoading(false);
  };

  const handleSelectOldActivity = async (oldActivity: Activity) => {
    setOldActivities([]);
    setAnchorEl(null);
    setCopyToast({
      copyName: oldActivity.activityName,
      open: true,
    });

    // eslint-disable-next-line functional/no-let
    let updatedActivity: Activity = {
      ...oldActivity,
      startDate: new Date(0),
      recurringActivity: null,
    };

    setActivity(updatedActivity);

    const images = await new ActivityApi().getImagesFromActivity({
      activityId: oldActivity.activityId,
      supervisedTag: currentClient?.tag,
    });

    if (!images) return;

    updatedActivity = {
      ...updatedActivity,
      pictureEdited: !!images.activityImage,
      picture: oldActivity.picture,
      pictureFile: images.activityImage,
    };

    if (oldActivity.subtasks && images.subtaskImages) {
      updatedActivity.subtasks = oldActivity.subtasks.map((subtask) => {
        const file = images.subtaskImages?.[subtask.subtaskId];
        return {
          ...subtask,
          picture: subtask.picture,
          pictureFile: file,
          pictureEdited: !!file,
        };
      });
    }

    setActivity(updatedActivity);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (menuRef.current?.contains(event.relatedTarget as Node)) {
      return;
    }

    setTimeout(() => {
      if (activity.activityName.length === 0) {
        setOldActivities([]);
      }
      setAnchorEl(null);
      setSelectedIndex(-1);
    }, 100);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!anchorEl || oldActivities.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < oldActivities.length - 1 ? prevIndex + 1 : prevIndex,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < oldActivities.length) {
          handleSelectOldActivity(oldActivities[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setAnchorEl(null);
        setSelectedIndex(-1);
        break;
      case 'Tab':
        setTimeout(() => {
          setAnchorEl(null);
          setSelectedIndex(-1);
        }, 100);
        break;
    }
  };

  const handleMenuItemKeyDown = (event: KeyboardEvent<HTMLLIElement>, index: number) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        handleSelectOldActivity(oldActivities[index]);
        break;
      case 'Escape':
        event.preventDefault();
        setAnchorEl(null);
        setSelectedIndex(-1);
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (index < oldActivities.length - 1) {
          setSelectedIndex(index + 1);
          const nextItem = menuRef.current?.querySelector(
            `[data-index="${index + 1}"]`,
          ) as HTMLElement;
          nextItem?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) {
          setSelectedIndex(index - 1);
          const prevItem = menuRef.current?.querySelector(
            `[data-index="${index - 1}"]`,
          ) as HTMLElement;
          prevItem?.focus();
        } else {
          if (textFieldRef.current) {
            textFieldRef.current.focus();
          }
          setSelectedIndex(-1);
        }
        break;
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && menuRef.current) {
      const selectedItem = menuRef.current.querySelector(
        `[data-index="${selectedIndex}"]`,
      ) as HTMLElement;
      if (selectedItem) {
        selectedItem.focus();
      }
    }
  }, [selectedIndex]);

  return (
    <>
      <InputLabel htmlFor="activityTitle">{t('Activity title')}:</InputLabel>
      <TextField
        id="activityTitle"
        ref={textFieldWrapperRef}
        placeholder={`${t('Type here')}...`}
        variant="outlined"
        value={activity.activityName}
        onChange={handleTitleChange}
        error={!!error}
        helperText={error}
        onFocus={(e) => setAnchorEl(e.currentTarget)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        inputProps={{
          ref: textFieldRef,
          'aria-expanded': !!anchorEl && oldActivities.length > 0,
          'aria-haspopup': 'listbox',
          'aria-controls': oldActivities.length > 0 ? 'activity-suggestions' : undefined,
          'aria-activedescendant':
            selectedIndex >= 0 ? `activity-item-${selectedIndex}` : undefined,
        }}
        InputProps={{
          role: 'combobox',
          'aria-autocomplete': 'list',
          endAdornment: oldActivitiesLoading ? <CircularProgress size={20} /> : null,
        }}
      />
      <Popper
        open={oldActivities.length > 0 && !!anchorEl}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: textFieldWrapperRef.current?.clientWidth || 'auto',
          maxWidth: '100%',
        }}
        role="presentation"
      >
        <Paper
          ref={menuRef}
          sx={{
            boxShadow: 4,
            width: '100%',
            maxHeight: 300,
            overflowY: 'auto',
          }}
          role="listbox"
          id="activity-suggestions"
          aria-label={t('Available activities') ?? ''}
        >
          {oldActivities.map((oldActivity, index) => {
            return (
              <MenuItem
                key={index}
                data-index={index}
                role="option"
                id={`activity-item-${index}`}
                aria-selected={selectedIndex === index}
                onClick={() => handleSelectOldActivity(oldActivity)}
                onKeyDown={(e) => handleMenuItemKeyDown(e as KeyboardEvent<HTMLLIElement>, index)}
                tabIndex={selectedIndex === index ? 0 : -1}
                sx={{
                  py: 1.5,
                  height: '64px',
                  borderBottom: `1px solid ${palette.grey[800]}`,
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                  borderLeft: `8px solid ${getActivityColor(palette, oldActivity.duration)}`,
                  outline: selectedIndex === index ? `2px solid ${palette.primary.main}` : 'none',
                  '&:focus': {
                    outline: `2px solid ${palette.primary.main}`,
                    backgroundColor: palette.action.hover,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems={'center'}
                  sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                >
                  {oldActivity.picture && oldActivity.picture !== '' && (
                    <img
                      src={oldActivity.picture}
                      alt={`${t('Image for')} ${oldActivity.activityName}`}
                      onError={() => {
                        oldActivity.picture = '';
                      }}
                      style={{
                        height: '44px',
                        transform: 'scale(1, 1)',
                        transformOrigin: 'center',
                        borderRadius: '5%',
                      }}
                    />
                  )}
                  <Typography
                    noWrap
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Box component="span" sx={{ color: 'inherit', mr: 1.6 }}>
                      {oldActivity.activityName}
                    </Box>
                    <Box component="span" sx={{ color: palette.grey[500] }}>
                      {oldActivity.description}
                    </Box>
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Paper>
      </Popper>
      <Snackbar
        open={copyToast.open}
        autoHideDuration={4000}
        onClose={() => setCopyToast({ copyName: '', open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        style={{ bottom: '80px' }}
      >
        <Alert
          onClose={() => setCopyToast({ copyName: '', open: false })}
          severity="success"
          variant="filled"
          sx={{
            minHeight: 86,
            width: '100%',
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              color: palette.primary.contrastText,
              marginRight: 1.2,
              fontSize: '1.5rem',
            },
          }}
        >
          {`${copyToast.copyName}: ${t('The activity has been copied')}`}
        </Alert>
      </Snackbar>
      {showSuggestions && aiEnabled && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={'space-between'}
            pt={4}
            color={'text.secondary'}
          >
            <Typography variant="subtitle1" component="h2" fontWeight={'bold'}>
              {t('AI-suggestions')}
            </Typography>
            <IconButton
              aria-label={t('Refresh suggestions') as string}
              onClick={refreshSuggestions}
              size="large"
            >
              <RefreshIcon />
            </IconButton>
          </Stack>

          {suggestions.length > 0 ? (
            <>
              {suggestions.map((suggestion, index) => (
                <SuggestionCard suggestion={suggestion} key={index} />
              ))}
            </>
          ) : (
            <>
              {suggestionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Typography variant="body2">{t('No suggestions available')}</Typography>
              )}
            </>
          )}
        </>
      )}
      {aiEnabled && (
        <AssistantSuggestionPopper
          anchorEl={formHandler.assistantAnchorEl ?? null}
          loading={assistantLoading}
          open={
            assistantSuggestion?.activityName === activity.activityName &&
            !!formHandler.assistantAnchorEl
          }
        >
          {!activity.activityName ? (
            <Typography variant="body2" color={palette.grey[50]}>
              {t(
                'Hello! I am an AI assistant who can help you create activities. Enter an activity title and I will help you create a suitable activity.',
              )}
            </Typography>
          ) : (
            <AssistantSuggestion
              title={t('Suggested activity:')}
              loading={assistantLoading}
              error={assistantSuggestionError}
            >
              {assistantSuggestion && <SuggestionCard suggestion={assistantSuggestion} />}
            </AssistantSuggestion>
          )}
        </AssistantSuggestionPopper>
      )}
    </>
  );
};

export default TitleForm;
